import { DEFAULT_SESSION_PLAYLIST_SORT_ORDER } from '@fullstory/library-ui';
import { Event, Platform } from '@fullstory/proto/fs/api/segment';
import { SearchieMatchingUsersSettings } from '@fullstory/proto/fs/services/alexa';
import { timestamp } from '@fullstory/proto/google/protobuf';
import {
  AppDispatch,
  AppState,
  fullstorySelectors,
  hasBeenWatched,
  MatchingUsersAsyncActions,
  matchingUsersSelectors,
  searchMetaSelectors,
  useUnnamedSegment,
} from '@fullstory/redux';
import { SessionExpanderRoute } from '@fullstory/routes';
import { disabledUnnamedSegment, makeSegmentDefinitionFromEvent } from '@fullstory/search-shared';
import moment from 'moment/moment';
import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

const { getSessionLink, watchHistoryData, sessionsData, isLoading, getUsers } =
  matchingUsersSelectors;

export interface MatchingUserRow {
  sessionLink: SessionExpanderRoute;
  isWatched: boolean;
  date: timestamp | null;
  url: string;
}

const { getSearchCacheKey } = searchMetaSelectors;

type useMatchingUserParams = {
  searchEvent: Event;
  lastSeen: timestamp | null;
};

export const useMatchingUser = ({ searchEvent, lastSeen }: useMatchingUserParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const searchCacheKey = useSelector(getSearchCacheKey);
  // We need this segment definition in order to get the event markers in playback
  const markersSegmentDefinition = useMemo(
    () =>
      makeSegmentDefinitionFromEvent(
        searchEvent,
        { startTime: 'NOW/DAY-29DAY', endTime: 'NOW/DAY+1DAY' }, // Scoping to our event volume time range
      ),
    [searchEvent],
  );
  const { unnamedSegmentId } = useUnnamedSegment(markersSegmentDefinition, disabledUnnamedSegment);

  const { users, sessions, loading } = useSelector(
    (state: AppState) => ({
      users: getUsers(state),
      sessions: sessionsData(state),
      loading: isLoading(state),
    }),
    shallowEqual,
  );
  const watchHistory = useSelector(watchHistoryData);

  const { mappedUsers } = useMemo(() => {
    // We only want to show users that have sessions on or before Tally's last seen time to prevent confusion with the
    // activity monitoring chart.
    // We normalize the last seen time and matching users time to the start of the day, so we can compare them easily.
    // This is sufficient since we only show the day as the last seen time.
    const tallyLastSeenDate = moment(lastSeen?.toString()).startOf('day').toDate();
    const mappedUsers = users
      .filter(user => {
        const sessionTimestamp = moment(user.lastMatchingEventStartAt?.toString())
          .startOf('day')
          .toDate();
        return sessionTimestamp <= tallyLastSeenDate;
      })
      .map(user => {
        const eventMarkers =
          sessions?.[user.indvId as string]?.clips?.clips?.[0]?.eventMarkers ?? [];
        return {
          isWatched: hasBeenWatched(watchHistory, user),
          sessionLink: getSessionLink(
            user,
            eventMarkers,
            markersSegmentDefinition,
            unnamedSegmentId,
          ),
          date: user.lastMatchingEventStartAt,
          url: user.lastMatchingPage,
        };
      });

    return { mappedUsers };
  }, [lastSeen, users, sessions, watchHistory, markersSegmentDefinition, unnamedSegmentId]);

  useEffect(() => {
    const fetchUserSettings: SearchieMatchingUsersSettings = {
      start: 0,
      limit: 20,
      sortOrder: DEFAULT_SESSION_PLAYLIST_SORT_ORDER,
      additionalSegmentDefinitions: [],
      eventSearch: {
        for: [],
        matchingBehaviors: [],
        select: [
          {
            events: [searchEvent],
            behaviors: [],
            platform: Platform.PLATFORM_INVALID,
            timeRange: {
              startTime: 'NOW/DAY-29DAY',
              endTime: 'NOW/DAY+1DAY',
            },
            completeSessions: false,
          },
        ],
      },
      earliestInSession: false,
    };

    dispatch(
      MatchingUsersAsyncActions.fetch(
        markersSegmentDefinition,
        searchCacheKey,
        fetchUserSettings,
        undefined,
        0,
        orgId,
        false,
        true,
        false,
      ),
    );
  }, [dispatch, markersSegmentDefinition, orgId, searchCacheKey, searchEvent]);

  return {
    matchingUsers: mappedUsers,
    loading,
    orgId,
  };
};
